import type { ActionButtonData } from '../action-button/ActionButton';
import type {
  BookingPurchaseDialogData,
  BookingPurchaseDialogView,
} from './BookingPurchaseDialog.types';
import type { PaymentOption } from './payment-section/PaymentSection';
import {
  CARD,
  DIRECT_DEBIT,
  hasStoredPaymentDetails,
  isCreditPack,
  isSingleMembership,
  isTrial,
  membershipSummary,
  newPaymentCollectorEnabled,
  purchaseTermsPreAccepted,
  shouldShowStartDate,
} from './buyMembershipRules';
import type { BuyMembershipHostData } from './buyMembership.types';
import { t } from '../../services/locale';

export function responseFooter(
  view: BookingPurchaseDialogView,
  data: BuyMembershipHostData,
  tryAgain: () => void
): ActionButtonData[] {
  const successful = view === 'success' || view === 'sca-pending';
  return [
    {
      id: 'close-modal-btn',
      label: t(successful ? 'WEB_DONE' : 'WEB_TRY_AGAIN'),
      variant: 'primary',
      onClick: successful ? data.onDone : tryAgain,
    },
  ];
}

export function mainFooter(args: {
  data: BuyMembershipHostData;
  options: PaymentOption[];
  autorenewalAccepted: boolean;
  loadingPrice: boolean;
  transactionInProgress: boolean;
  buy: () => void;
}): ActionButtonData[] {
  const { data, options, autorenewalAccepted, loadingPrice, transactionInProgress, buy } =
    args;
  if (!data.user?._id) {
    return [
      {
        id: 'signup-purchase-btn',
        label: t(
          isTrial(data) ? 'WEB_TRIAL_PURCHASE' : 'WEB_SIGN_UP_PURCHASE'
        ),
        variant: 'primary',
        onClick: data.showLoginRegister,
      },
    ];
  }
  if (data.user.membership?.status === 'LOCKED') {
    return [];
  }
  const needsConsent = needsCustomLinkConsent(data, autorenewalAccepted);
  return [
    {
      id: 'buy-membership-btn',
      label: t(buyButtonLabel(data, needsConsent)),
      variant: buyButtonDisabled({
        data,
        options,
        loadingPrice,
        transactionInProgress,
        needsConsent,
      })
        ? 'disabled'
        : 'primary',
      onClick: buy,
    },
  ];
}

function needsCustomLinkConsent(
  data: BuyMembershipHostData,
  autorenewalAccepted: boolean
): boolean {
  const plan = data.membership.planToBuy;
  return (
    !isSingleMembership(plan) &&
    !isCreditPack(plan) &&
    data.flags.isFtcEnabled &&
    data.isCustomLink &&
    !autorenewalAccepted
  );
}

function buyButtonLabel(
  data: BuyMembershipHostData,
  needsConsent: boolean
): string {
  if (isTrial(data)) {
    return 'WEB_TRIAL_PURCHASE';
  }
  return needsConsent ? 'WEB_ACCEPT_ALL_TERMS' : 'WEB_BUY_MEMBERSHIP';
}

function buyButtonDisabled(args: {
  data: BuyMembershipHostData;
  options: PaymentOption[];
  loadingPrice: boolean;
  transactionInProgress: boolean;
  needsConsent: boolean;
}): boolean {
  const paymentUnavailable =
    (args.data.membership.planToBuy.price || 0) > 0 && args.options.length === 0;
  return (
    args.transactionInProgress ||
    args.loadingPrice ||
    paymentUnavailable ||
    args.needsConsent
  );
}

type ResponseCopyArgs = {
  data: BuyMembershipHostData;
  view: BookingPurchaseDialogView;
  memberAllowed: boolean;
  notAllowedReason: string;
  errors: string[];
};

function responseTitle(args: ResponseCopyArgs): string {
  if (args.view === 'success') {
    return t('WEB_YOU_SUCCESSFULLY_BOUGHT');
  }
  if (args.view === 'sca-pending') {
    return t('WEB_PAYMENT_TO_BE_VERIFIED');
  }
  return t(
    args.memberAllowed
      ? 'WEB_YOU_FAILED_BUYING'
      : 'WEB_YOU_ARE_NOT_ALLOWED_TO_BUY_THIS_MEMBERSHIP'
  );
}

function responseSubtitle(args: ResponseCopyArgs): string {
  if (args.view === 'success') {
    return t('WEB_MEMBERSHIP_DETAILS_CHECK_ON_PROFILE');
  }
  return args.view === 'sca-pending'
    ? t('WEB_SCA_EXPLAINED')
    : '';
}

function responseName(args: ResponseCopyArgs): string {
  if (args.view === 'sca-pending') {
    return '';
  }
  return args.memberAllowed
    ? `${args.data.membership.name || ''} - ${
        args.data.membership.planToBuy.name || ''
      }`
    : args.notAllowedReason;
}

function responseId(args: ResponseCopyArgs): string | null {
  if (args.view !== 'error') {
    return null;
  }
  return args.memberAllowed
    ? 'membership-purchase-failed'
    : 'membership-purchase-not-allowed';
}

export function responseCopy(args: ResponseCopyArgs) {
  return {
    responseTitle: responseTitle(args),
    responseSubtitle: responseSubtitle(args),
    responseName: responseName(args),
    responseErrors:
      args.view === 'error' && args.memberAllowed
        ? args.errors.map((error) => t(error))
        : [],
    responseId: responseId(args),
  };
}

export function termsData(args: {
  data: BuyMembershipHostData;
  isResponse: boolean;
  termsAccepted: boolean;
  autorenewalAccepted: boolean;
  setTermsAccepted: (accepted: boolean) => void;
  setAutorenewalAccepted: (accepted: boolean) => void;
}): BookingPurchaseDialogData['terms'] {
  const {
    data,
    isResponse,
    termsAccepted,
    autorenewalAccepted,
    setTermsAccepted,
    setAutorenewalAccepted,
  } = args;
  const plan = data.membership.planToBuy;
  const showPurchaseTerms = !data.isCustomLink || !data.flags.isFtcEnabled;
  const showAutorenewalTerms =
    !isSingleMembership(plan) && !isCreditPack(plan) && data.flags.isFtcEnabled;
  if (isResponse || (!showPurchaseTerms && !showAutorenewalTerms)) {
    return undefined;
  }
  const items = [];
  if (showPurchaseTerms) {
    const forced = purchaseTermsPreAccepted(data);
    items.push({
      key: 'purchase',
      accepted: forced || termsAccepted,
      disabled: forced,
      agreeLabelKey: 'WEB_I_AGREE_TO',
      linkLabelKey: 'WEB_MEMBERSHIP_PURCHASE_TERMS',
      checkboxId: 'purchase-membership-accept-terms',
      onChange: setTermsAccepted,
      onClickLink: () => data.showPurchaseTerms(),
    });
  }
  if (showAutorenewalTerms) {
    items.push({
      key: 'autorenewal',
      accepted: !data.isCustomLink || autorenewalAccepted,
      disabled: !data.isCustomLink,
      agreeLabelKey: data.isCustomLink
        ? 'WEB_FTC_CONSENT_TO_AUTORENEWAL_TERMS'
        : 'WEB_I_AGREE_AUTORENEWAL_CS',
      onChange: setAutorenewalAccepted,
      onClickLink: data.showAutorenewalTerms,
    });
  }
  return { mode: 'membership', items };
}

export function paymentData(args: {
  data: BuyMembershipHostData;
  isResponse: boolean;
  isProRated: boolean;
  selectedPayment?: PaymentOption;
  options: PaymentOption[];
  paymentDropdownOpen: boolean;
  selectedStartDate?: string;
  promoCode?: string;
  setPaymentDropdownOpen: (open: boolean) => void;
  setSelectedPayment: (option: PaymentOption) => void;
  setSelectedStartDate: (date: string) => void;
  refreshPrice: (promo?: string, date?: string) => Promise<void>;
}): BookingPurchaseDialogData['payment'] {
  const {
    data,
    isResponse,
    isProRated,
    selectedPayment,
    options,
    paymentDropdownOpen,
    selectedStartDate,
    promoCode,
    setPaymentDropdownOpen,
    setSelectedPayment,
    setSelectedStartDate,
    refreshPrice,
  } = args;
  if (isResponse || !data.user?._id) {
    return undefined;
  }
  const min = data.todayLocal();
  const max = data.addLocalDays(min, 8);
  return {
    showPayment: !newPaymentCollectorEnabled(data),
    paymentMethodLabel: t('WEB_PAYMENT_METHOD'),
    selectedPaymentLabel: selectedPayment?.value || '',
    paymentOptions: options,
    paymentDropdownOpen,
    onTogglePaymentDropdown: () => {
      if (options.length > 1) {
        setPaymentDropdownOpen(!paymentDropdownOpen);
      }
    },
    onSelectPayment: (option) => {
      selectPayment(data, option, setSelectedPayment, setPaymentDropdownOpen);
    },
    showStartDate: shouldShowStartDate(data, isProRated),
    startDateLabel: t('WEB_START_DATE'),
    startDateValue: selectedStartDate,
    minStartDate: min,
    maxStartDate: max,
    onStartDateChange: (date) => {
      changeStartDate({
        data,
        date,
        min,
        max,
        promoCode,
        setSelectedStartDate,
        refreshPrice,
      });
    },
  };
}

function paymentDetailsMissing(
  data: BuyMembershipHostData,
  paymentKey: string
): boolean {
  return (
    [CARD, DIRECT_DEBIT].includes(paymentKey) && !hasStoredPaymentDetails(data, paymentKey)
  );
}

function selectPayment(
  data: BuyMembershipHostData,
  option: PaymentOption,
  setSelectedPayment: (option: PaymentOption) => void,
  setPaymentDropdownOpen: (open: boolean) => void
) {
  setSelectedPayment(option);
  setPaymentDropdownOpen(false);
  const key = option.key?.toLowerCase() || '';
  if ((data.membership.planToBuy.price || 0) <= 0) {
    return;
  }
  if (paymentDetailsMissing(data, key)) {
    void data.showPaymentCollector(key);
  }
}

function changeStartDate(args: {
  data: BuyMembershipHostData;
  date: string;
  min: string;
  max: string;
  promoCode?: string;
  setSelectedStartDate: (date: string) => void;
  refreshPrice: (promo?: string, date?: string) => Promise<void>;
}) {
  const { data, date, min, max, promoCode, setSelectedStartDate, refreshPrice } = args;
  if (isDateInRange(date, min, max)) {
    setSelectedStartDate(date);
    void refreshPrice(promoCode, date);
    return;
  }
  const message = t('WEB_INVALID_START_DATE_MESSAGE', {
    min_date: data.formatLocalDate(min),
    max_date: data.formatLocalDate(max),
  });
  data.showGeneralError(message, 'WEB_INVALID_START_DATE');
  setSelectedStartDate(min);
  void refreshPrice(promoCode, min);
}

function isDateInRange(date: string, min: string, max: string): boolean {
  if (!date) return false;
  if (date < min) return false;
  return date <= max;
}

function warnings(data: BuyMembershipHostData): BookingPurchaseDialogData['warnings'] {
  if (data.user?.membership?.status !== 'LOCKED') {
    return [];
  }
  return [
    {
      message: t('WEB_USER_HAS_LOCKED_MEMBERSHIP_WARNING'),
      modifier: 'documents',
    },
  ];
}

function visiblePriceBreakdown(
  isResponse: boolean,
  priceBreakdown: BookingPurchaseDialogData['priceBreakdown'],
  loadingPrice: boolean
) {
  if (isResponse || !priceBreakdown) {
    return null;
  }
  return { ...priceBreakdown, isLoading: loadingPrice };
}

export function buyMembershipDialogData(args: {
  data: BuyMembershipHostData;
  view: BookingPurchaseDialogView;
  memberAllowed: boolean;
  notAllowedReason: string;
  errors: string[];
  shared: boolean;
  shareMembership: () => void;
  selectedPayment?: PaymentOption;
  options: PaymentOption[];
  paymentDropdownOpen: boolean;
  setPaymentDropdownOpen: (open: boolean) => void;
  setSelectedPayment: (option: PaymentOption) => void;
  selectedStartDate?: string;
  setSelectedStartDate: (date: string) => void;
  promoCode?: string;
  priceBreakdown: BookingPurchaseDialogData['priceBreakdown'];
  totalPrice: number;
  loadingPrice: boolean;
  isProRated: boolean;
  refreshPrice: (promo?: string, date?: string) => Promise<void>;
  termsAccepted: boolean;
  setTermsAccepted: (accepted: boolean) => void;
  autorenewalAccepted: boolean;
  setAutorenewalAccepted: (accepted: boolean) => void;
  transactionInProgress: boolean;
  buy: () => void;
  tryAgain: () => void;
}): BookingPurchaseDialogData {
  const isResponse = args.view !== 'main';
  const footerButtons = dialogFooter(args, isResponse);
  return {
    variant: 'buy-membership',
    unifiedDialogEnabled: true,
    view: args.view,
    header: {
      showShare: !isResponse && args.memberAllowed,
      showClose: !args.data.isCustomLink,
      shared: args.shared,
      sharedTooltipText: t('WEB_LINK_COPIED_TO_CLIPBOARD'),
      onShare: args.shareMembership,
      onClose: args.data.onClose,
      closeButtonId: 'close-buy-membership-btn',
      closeAriaLabel: t('WEB_CLOSE') || 'Close',
      shareAriaLabel: t('WEB_SHARE') || 'Share',
    },
    title: args.data.membership.name || '',
    warnings: warnings(args.data),
    description: isResponse
      ? undefined
      : { description: args.data.membership.description || '', previewLength: 70 },
    membershipSummary: isResponse ? undefined : membershipSummary(args.data),
    priceBreakdown: visiblePriceBreakdown(
      isResponse,
      args.priceBreakdown,
      args.loadingPrice
    ),
    payment: paymentData({ ...args, isResponse }),
    terms: termsData({ ...args, isResponse }),
    footerButtons,
    ...responseCopy(args),
  };
}

function dialogFooter(
  args: Parameters<typeof buyMembershipDialogData>[0],
  isResponse: boolean
): ActionButtonData[] {
  if (!isResponse) {
    return mainFooter(args);
  }
  const errorAction = args.memberAllowed ? args.tryAgain : args.data.onClose;
  return responseFooter(args.view, args.data, errorAction);
}
